import type { ConsentItemConfig } from 'types/nhis';

// 회원가입 약관 동의 페이지(서비스 이용약관 + 개인정보처리방침) 재동의 트리거 버전.
// 값 변경 시 전 회원 재동의가 강제되므로(withPageGuard) 법무/사업 승인 후에만 변경할 것.
export const AGREEMENT_VERSION = "20260615";

// 서비스 이용 가이드 버전
export const GUIDE_VERSION = "20250617";

// 약관 정본 링크 — 자체 도메인(company.ymyd.co.kr) 호스팅.
// 구조: 서비스 이용약관(기본약관) + 정기결제 약관(개별약관) + 개인정보처리방침.
export const PRIVACY_POLICY_URL = "https://company.ymyd.co.kr/phai/privacy";
export const SERVICE_TERMS_URL = "https://company.ymyd.co.kr/phai/contract";
// 구독 슬라이드 "정기결제 약관" 동의 링크(개별약관).
export const SUBSCRIPTION_TERMS_URL =
  "https://company.ymyd.co.kr/phai/subscribe-contract";

// 정기결제 약관(개별약관) 시행일 = 동의 버전. subscriptions.terms_version에 기록.
// 정기결제 약관 개정 시 이 값만 갱신(버전 단일 출처, 백엔드는 검증 없이 저장).
export const SUBSCRIPTION_TERMS_VERSION = "20260609";

// 허가사항 정보 부족 경고 기준
export const MIN_APPROVAL_INFO_COUNT = 8;

// NHIS 환자 동의 문서 버전. 2026-11-02 동의 4번 개정(spec 2026-11-02)
export const CONSENT_VERSION = "20261102";

// 케어링노트(파트너) 경로 동의 문서 버전 — 6항목판. 버전으로 5항목판과 판을 구분한다(spec 2026-09-02 확정 4). 2026-11-02 동의 4번 개정(spec 2026-11-02)
export const CONSENT_VERSION_CARINGNOTE = "20261102-CN";

// K-MRS(서울시약사회) 경로 동의 문서 버전 — 6항목판(제3자 제공 대상이 다르다). 버전으로 판을 구분한다(spec D28). 2026-11-02 동의 4번 개정(spec 2026-11-02)
export const CONSENT_VERSION_KMRS = "20261102-KMRS";

/**
 * K-MRS판 동의 6번(개인정보 제3자 제공)의 표기 — [외부] 정확한 법인명·항목·목적·보유기간 확정 전 자리값(spec D28 Open Question).
 * 제공받는 자/항목/목적/보유기간을 한곳에 모았다 — 확정되면 이 블록만 교체하면 라벨·상세 화면이 함께 바뀐다.
 */
export const KMRS_THIRD_PARTY = {
  recipient: "서울특별시약사회, 서울대학교",
  items: "복용 약 정보(처방·조제 내역)",
  purpose: "약물 상담 및 약물위험 연구",
  retention: "(보유·이용 기간 확정 전)",
} as const;

// NHIS 환자 동의 항목 설정
export const CONSENT_ITEMS: ConsentItemConfig[] = [
  { id: 'PERSONAL_INFO',        label: '[필수] 약사님의 상담 지원을 위한 개인정보 수집이용 동의',           required: true },
  { id: 'RESIDENT_NUMBER',      label: '[필수] 보건의료망 연동을 위한 주민등록번호 일회성 이용 동의',       required: true },
  { id: 'SENSITIVE_INFO',       label: '[필수] 약사님의 다각적 약물 심층 검토를 위한 민감정보 수집 동의',   required: true },
  { id: 'AI_OVERSEAS_TRANSFER', label: '[필수] 가명처리된 데이터의 외부 AI 분석(국외 이전) 동의',          required: true },
  { id: 'THIRD_PARTY',          label: '[필수] 담당 약사님(제3자)에게 정보 및 분석 자료 제공 동의',        required: true },
];

// 케어링노트(파트너) 경로 동의 항목 — 5번 문구 변형(늘픔가치 소속 상담 약사) + 6번(늘픔가치 제3자 제공)
export const CONSENT_ITEMS_CARINGNOTE: ConsentItemConfig[] = [
  ...CONSENT_ITEMS.filter((item) => item.id !== 'THIRD_PARTY'),
  { id: 'THIRD_PARTY',            label: '[필수] 담당 약사님(늘픔가치 소속 상담 약사)에게 정보 및 분석 자료 제공 동의', required: true },
  { id: 'CARINGNOTE_THIRD_PARTY', label: '[필수] 사단법인 늘픔가치(케어링노트)에 개인정보 제3자 제공 동의',            required: true },
];

// K-MRS(서울시약사회) 경로 동의 항목 — 5번 문구 변형(서울특별시약사회 소속 상담 약사) + 6번(서울시약사회·서울대 제3자 제공, 제출 키 PARTNER_THIRD_PARTY)
export const CONSENT_ITEMS_KMRS: ConsentItemConfig[] = [
  ...CONSENT_ITEMS.filter((item) => item.id !== 'THIRD_PARTY'),
  { id: 'THIRD_PARTY',         label: '[필수] 담당 약사님(서울특별시약사회 소속 상담 약사)에게 정보 및 분석 자료 제공 동의', required: true },
  { id: 'PARTNER_THIRD_PARTY', label: `[필수] ${KMRS_THIRD_PARTY.recipient}에 개인정보 제3자 제공 동의`,                      required: true },
];
